import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { formatCurrency, cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

export function AdvOfferDetailPage() {
  const { offerId } = useParams({ strict: false }) as { offerId: string };

  const { data, isLoading } = useQuery({
    queryKey: ["adv-portal", "offer", offerId],
    queryFn: async () => {
      const res = await api.get(`/v1/portal/advertiser/offers/${offerId}`);
      if (!res.ok) throw new Error("Failed to load offer");
      return (await res.json()).data;
    },
  });

  if (isLoading) {
    return <div className="animate-pulse space-y-4"><div className="h-8 bg-gray-200 rounded w-1/3" /><div className="h-64 bg-gray-200 rounded" /></div>;
  }
  if (!data) return <div className="text-center py-16 text-gray-500">Offer not found</div>;

  const { offer, payoutRevenues } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/portal/advertiser/offers" className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{offer.name}</h1>
          <p className="text-sm text-gray-500">Offer ID: {offer.id}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Details</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between">
              <dt className="text-gray-500">Status</dt>
              <dd><span className={cn(
                "text-xs font-medium px-1.5 py-0.5 rounded uppercase",
                offer.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
              )}>{offer.status}</span></dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Visibility</dt>
              <dd className="capitalize text-gray-700">{offer.visibility}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Conversion Method</dt>
              <dd className="text-gray-700">{offer.conversionMethod}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Destination URL</dt>
              <dd className="text-gray-700 truncate max-w-[250px]">{offer.destinationUrl}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Payout / Revenue</h3>
          {payoutRevenues?.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 font-medium text-gray-500 text-xs">Type</th>
                  <th className="text-right py-2 font-medium text-gray-500 text-xs">Payout</th>
                  <th className="text-right py-2 font-medium text-gray-500 text-xs">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {payoutRevenues.map((pr: { id: number; payoutType: string; payoutAmount: number; revenueType: string; revenueAmount: number; isDefault: boolean }) => (
                  <tr key={pr.id} className="border-b border-gray-50">
                    <td className="py-2 text-gray-700 text-xs uppercase">
                      {pr.payoutType}
                      {pr.isDefault && <span className="ml-1.5 bg-gray-100 px-1 py-0.5 rounded text-[10px]">default</span>}
                    </td>
                    <td className="py-2 text-right text-gray-700">{formatCurrency(pr.payoutAmount)}</td>
                    <td className="py-2 text-right font-medium text-blue-600">{formatCurrency(pr.revenueAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-gray-400">No payout/revenue configured.</p>
          )}
        </div>
      </div>
    </div>
  );
}
